import React, { useState, useContext, useEffect } from 'react';
import FileSelector from '../FileSelector/FileSelector';
import SheetSelector from '../SheetSelector/SheetSelector';
import ColumnMapping from '../ColumnMapping/ColumnMapping';
import { GenerateSpendCubeContext } from 'contexts/GenerateSpendCubeContext';
import {
	ContentWrapper,
	FileSelectionWrapper,
	HeaderContainer,
	MappingContentWrapper,
	StepCount,
	Wrapper,
	PreviewWrapper,
	DataPreviewWrapper,
	ButtonContainer,
} from './MappingManager.styles';
import { postMappedJson } from 'utils/apiRequests';
import PreviewData from '../PreviewData/PreviewData';
import { NotificationTypes } from 'components/Toast/constants';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import Processing from '../ProcessingPage/Processing';
import FileUploadError from '../FileUploadError/FileUploadError';
import { ApButtonMain, ApButtonSecondary } from '@alixpartners/ui-components';
import { workingCapitalCols } from '../../constants';

const MappingManager = ({
	filterData,
	setCurrentLoadingMessage,
	isFileExpired,
}) => {
	const {
		finalMapping,
		type,
		getCurrentEtlState,
		setPageLoading,
		setIsShowRestartModal,
	} = useContext(GenerateSpendCubeContext);
	const { showToast } = useToastMessageContext();
	const [selectedFile, setSelectedFile] = useState(null);
	const [selectedSheet, setSelectedSheet] = useState(null);
	const [isMappingComplete, setIsMappingComplete] = useState(false);
	const [sheetOption, setSheetOption] = useState(null);
	const [isProceedDisabled, setIsProceedDisabled] = useState(true);
	const [mapping, setMapping] = useState({});
	const [isWorkingCapitalColsExists, setIsWorkingCapitalColsExists] = useState(
		false
	);

	const checkMappingCompletion = () => {
		for (const file of filterData.excels) {
			const mappedFile = finalMapping.excels.find(
				f => f.excelName === file.excelName
			);
			if (!mappedFile) return false;

			for (const sheet of file.sheets) {
				const mappedSheet = mappedFile.sheets.find(
					s => s.sheetName === sheet.sheetName
				);
				if (!mappedSheet || mappedSheet.mapping.length === 0) return false;
			}
		}

		if (isWorkingCapitalColsExists) {
			const mappedCols = finalMapping?.excels;
			return !mappedCols.some(excel =>
				excel?.sheets.some(sheet => {
					const sources = sheet?.mapping.map(data => data.source);
					return !workingCapitalCols.every(col => sources.includes(col));
				})
			);
		}
		return true;
	};

	useEffect(
		() => {
			if (Object.keys(filterData).length !== 0) {
				setIsMappingComplete(checkMappingCompletion());
			}
		}, //eslint-disable-next-line
		[finalMapping, isWorkingCapitalColsExists]
	);

	useEffect(
		() => {
			if (Object.keys(filterData).length !== 0) {
				localStorage.setItem('fileData', JSON.stringify(filterData));
			}
		},
		[filterData]
	);

	const handleProceed = () => {
		if (isMappingComplete) {
			const nameMapping = finalMapping?.excels?.map(file => {
				const actualFile = filterData.excels.filter(excel => {
					return file.excelName === excel?.excelName;
				});
				const actualFilename = actualFile[0]?.actualName;
				return {
					actualName: actualFilename,
					...file,
				};
			});
			const mappingWithNames = { excels: nameMapping };
			const mappedData = {
				type,
				...mappingWithNames,
			};
			setPageLoading(true);
			setCurrentLoadingMessage({
				message: 'Mapping in progress',
				text: '',
			});
			postMappedJson(mappedData)
				.then(() => {
					localStorage.removeItem('mapping');
					localStorage.removeItem('fileData');
					getCurrentEtlState();
				})
				.catch(() => {
					showToast(
						'Mapping could not be submitted.',
						NotificationTypes.danger,
						3000
					);
				});
		}
	};

	const restartUploading = () => {
		setCurrentLoadingMessage({
			message: 'ETL process is being restarted',
			text: '',
		});
		setIsShowRestartModal(true);
	};

	return (
		<React.Fragment>
			{Object.keys(filterData).length === 0 && !isFileExpired ? (
				<Processing message="Fetching column headers." />
			) : isFileExpired ? (
				<FileUploadError
					errorMessage="Column mapping was not found as the maximum duration exceeded. Please restart the file
					upload process."
					setCurrentLoadingMessage={setCurrentLoadingMessage}
				/>
			) : (
				<Wrapper>
					<HeaderContainer>
						<StepCount>Step 2: Column Mapping</StepCount>
					</HeaderContainer>
					<ContentWrapper>
						<FileSelectionWrapper>
							<FileSelector
								filterData={filterData}
								setSelectedFile={setSelectedFile}
								setSelectedSheet={setSelectedSheet}
								setSheetOption={setSheetOption}
							/>
							{selectedFile && (
								<SheetSelector
									selectedFile={selectedFile}
									setSelectedSheet={setSelectedSheet}
									sheetOption={sheetOption}
									setSheetOption={setSheetOption}
								/>
							)}
						</FileSelectionWrapper>

						<MappingContentWrapper>
							{selectedSheet && (
								<ColumnMapping
									selectedFile={selectedFile}
									selectedSheet={selectedSheet}
									mapping={mapping}
									setMapping={setMapping}
									setIsProceedDisabled={setIsProceedDisabled}
									finalColumnMapping={finalMapping}
									setIsWorkingCapitalColsExists={setIsWorkingCapitalColsExists}
									isWorkingCapitalColsExists={isWorkingCapitalColsExists}
								/>
							)}
						</MappingContentWrapper>
					</ContentWrapper>

					<ButtonContainer>
						<ApButtonMain
							onClick={handleProceed}
							disabled={!isMappingComplete || isProceedDisabled}
						>
							Proceed
						</ApButtonMain>

						<ApButtonSecondary
							style={{ color: 'orange', borderColor: 'orange' }}
							onClick={restartUploading}
						>
							Restart
						</ApButtonSecondary>
					</ButtonContainer>

					{selectedSheet && (
						<PreviewWrapper>
							<StepCount>Data Preview</StepCount>
							<DataPreviewWrapper>
								<PreviewData
									columns={selectedSheet.columns}
									records={selectedSheet.columns[0]?.values || []}
								/>
							</DataPreviewWrapper>
						</PreviewWrapper>
					)}
				</Wrapper>
			)}
		</React.Fragment>
	);
};

export default MappingManager;
